import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, CARE_SEEKER_STATUS, INTAKE_CHANNELS, formatApiError, formatDate, formatRelative, daysSince } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import MetadataFooter from "@/components/MetadataFooter";
import { Plus, Search, Users, X, Pencil, PhoneIncoming, Clock, AlertTriangle, Bell, LayoutGrid, LayoutList, ArrowUp, ArrowDown, Download, Target, CheckSquare, Flag, UserCheck, AlertOctagon } from "lucide-react";

const CHANNEL_LABEL = Object.fromEntries(INTAKE_CHANNELS.map((c) => [c.value, c.label]));

const statusColor = (s) =>
  ({
    active: "bg-[#E8ECE7] text-[#1E3A8A]",
    follow_up: "bg-[#FEF3C7] text-[#92400E]",
    inactive: "bg-[#F1F5F9] text-[#64748B]",
    closed: "bg-[#FEE2E2] text-[#991B1B]",
  }[s] || "bg-[#F1F5F9] text-[#64748B]");

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportCareSeekersCsv(items) {
  const headers = [
    "ID", "First name", "Last name", "Preferred name", "Status", "Phone", "Email",
    "DOB", "County", "City", "ZIP", "Intake channel", "Referral source",
    "Last contact", "Created", "Risk flags",
  ];
  const rows = items.map((p) => [
    p.display_id, p.first_name, p.last_name, p.preferred_name, p.status,
    p.phone, p.email, p.dob, p.county, p.city, p.zip_code,
    p.intake_channel ? (CHANNEL_LABEL[p.intake_channel] || p.intake_channel) : "",
    p.referral_source, p.last_contact_at || "", p.created_at,
    (p.risk_flags || []).join("; "),
  ]);
  const lines = [headers, ...rows].map((row) => row.map(csvEscape).join(","));
  const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.download = `harborwell-care-seekers-${stamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function CareSeekers() {
  const { can, user } = useAuth();
  // HC-EPIC-002 · Slice 2B — defense-in-depth alongside ProtectedRoute.
  // The route guard (App.js L190 → ProtectedRoute → canSeeModule) is
  // still the authoritative boundary. This mirrors the pattern already
  // in Layout.jsx L223-239 for /care-seekers/new-count. Uses only the
  // DB-driven `can()` slug — no role-name check.
  const canViewList = can("care_seekers.view");
  const canCreate = can("care_seekers.create");
  const [searchParams, setSearchParams] = useSearchParams();
  // Per user request: "Each staff member needs to be able to see their
  // assigned care seekers" — default non-admin/non-supervisor to the
  // "Assigned to me" view; they can toggle to All. Admin + Supervisor
  // default to "All" since they manage the whole roster.
  const supervisorRoles = new Set(["admin", "executive_director"]);
  const defaultAssignedToMe = user && !supervisorRoles.has(user.role) && user.role !== "volunteer";
  const [assignedFilter, setAssignedFilter] = useState(() => {
    const urlVal = searchParams.get("assigned_to");
    if (urlVal === "me") return "me";
    if (urlVal === "all" || urlVal === "") return "all";
    if (urlVal === "unassigned") return "unassigned";
    return defaultAssignedToMe ? "me" : "all";
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const channelFilter = searchParams.get("channel") || "";
  const [showForm, setShowForm] = useState(searchParams.get("new") === "1");
  const [editing, setEditing] = useState(null);
  const [sortMode, setSortMode] = useState("recent");
  const [staleOnly, setStaleOnly] = useState(false);
  const [staleReferralsOnly, setStaleReferralsOnly] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("cs_view_mode") || "cards"); // 'cards' | 'list'
  // Column sort applies only in list view. `null` = use the chip-based sortMode.
  const [tableSort, setTableSort] = useState(null); // {key: 'name'|'status'|'county'|'channel'|'last_contact'|'display_id', dir: 'asc'|'desc'}
  // Phase 25 — bulk-selection state (set of Care Seeker ids)
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState(null); // 'assign' | 'flag' | null
  const [users, setUsers] = useState([]); // for navigator picker

  // Load users — used for the bulk-assign picker (admins) and the
  // assigned-navigator label on every card/row (all staff).
  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data || [])).catch(() => {});
  }, []);

  // Lookup table: user id → display name. Cheap to derive each render.
  const usersById = users.reduce((acc, u) => {
    acc[u.id] = u.name;
    return acc;
  }, {});

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = (ids) => setSelectedIds(new Set(ids));
  const clearSelection = () => setSelectedIds(new Set());

  // Persist preference across sessions
  useEffect(() => {
    localStorage.setItem("cs_view_mode", viewMode);
  }, [viewMode]);

  // Clear ?new=1 from URL after opening so it doesn't reopen on refresh
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const setChannelFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("channel", value);
    else next.delete("channel");
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    // Slice 2B defensive guard — skip the fetch when the caller does
    // not hold `care_seekers.view`. Route guard remains authoritative;
    // this prevents any code path that mounts the page outside the
    // router from issuing a 403 that surfaces a global toast. Loading
    // is explicitly cleared so the page never hangs on the spinner.
    if (!canViewList) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      if (channelFilter) params.channel = channelFilter;
      if (assignedFilter && assignedFilter !== "all") params.assigned_to = assignedFilter;
      const { data } = await api.get("/care-seekers", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [statusFilter, channelFilter, assignedFilter, canViewList]);

  const isStale = (p) => {
    const isActive = p.status === "active" || p.status === "follow_up";
    if (!isActive) return false;
    if (!p.last_contact_at) return true;
    const d = daysSince(p.last_contact_at);
    return d != null && d > 7;
  };

  const visibleItems = (() => {
    let list = staleOnly ? items.filter(isStale) : items.slice();
    if (staleReferralsOnly) {
      list = list.filter((p) => (p.stale_referrals_count || 0) > 0);
    }
    if (sortMode === "oldest_contact") {
      // No contact (null) first, then ascending by date (oldest contact first)
      list.sort((a, b) => {
        const aIso = a.last_contact_at;
        const bIso = b.last_contact_at;
        if (!aIso && !bIso) return 0;
        if (!aIso) return -1;
        if (!bIso) return 1;
        return aIso.localeCompare(bIso);
      });
    }
    // Column sort overrides the chip-based sort when active (list view only).
    if (viewMode === "list" && tableSort) {
      const { key, dir } = tableSort;
      const mult = dir === "asc" ? 1 : -1;
      const get = (p) => {
        if (key === "name") return ((p.preferred_name || p.first_name || "") + " " + (p.last_name || "")).toLowerCase();
        if (key === "status") return p.status || "";
        if (key === "county") return (p.county || "").toLowerCase();
        if (key === "channel") return (CHANNEL_LABEL[p.intake_channel] || p.intake_channel || "").toLowerCase();
        if (key === "display_id") return p.display_id || "";
        if (key === "phone") return (p.phone || "").replace(/\D/g, "");
        if (key === "last_contact") return p.last_contact_at || ""; // empty sorts before strings (asc)
        return "";
      };
      list.sort((a, b) => {
        const va = get(a); const vb = get(b);
        if (va < vb) return -1 * mult;
        if (va > vb) return 1 * mult;
        return 0;
      });
    }
    return list;
  })();

  return (
    <div className="space-y-6" data-testid="care-seekers-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">People we serve</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Care Seekers</h1>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowForm(true)}
            data-testid="add-care-seeker-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] transition-colors"
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
            Add Care Seeker
          </button>
        )}
      </div>

      {/* Search & Filter */}
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0]">
          <Search className="w-4 h-4 text-[#1E3A8A]" />
          <input
            data-testid="care-seekers-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name or phone…"
            className="bg-transparent flex-1 outline-none text-sm"
          />
        </div>
        <select
          data-testid="care-seekers-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm focus:outline-none focus:ring-2 focus:ring-[#7E937D]"
        >
          <option value="">All statuses</option>
          {CARE_SEEKER_STATUS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select
          data-testid="care-seekers-channel-filter"
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          className="px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm focus:outline-none focus:ring-2 focus:ring-[#7E937D]"
          aria-label="Filter by how they reached us"
        >
          <option value="">All channels</option>
          {INTAKE_CHANNELS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
          <option value="unspecified">Unspecified</option>
        </select>
        <button onClick={load} className="px-4 py-2 rounded-xl bg-[#E8ECE7] text-[#1E3A8A] text-sm font-medium" data-testid="care-seekers-search-btn">
          Search
        </button>
      </div>

      {/* Assigned-to-me toggle — non-admin/supervisor staff default to "Mine" */}
      {user && user.role !== "volunteer" && (
        <div className="flex items-center gap-3 flex-wrap text-sm" data-testid="assigned-filter-row">
          <span className="text-xs text-[#1E3A8A] uppercase tracking-wider">View</span>
          <div className="inline-flex rounded-full bg-[#F9F8F6] border border-[#E5E5E0] p-0.5">
            <button
              type="button"
              onClick={() => setAssignedFilter("me")}
              data-testid="assigned-filter-me"
              className={`px-3 py-1.5 rounded-full text-xs transition-colors ${assignedFilter === "me" ? "bg-[#1E3A8A] text-white" : "text-[#334155] hover:bg-white"}`}
            >
              Assigned to me
            </button>
            <button
              type="button"
              onClick={() => setAssignedFilter("all")}
              data-testid="assigned-filter-all"
              className={`px-3 py-1.5 rounded-full text-xs transition-colors ${assignedFilter === "all" ? "bg-[#1E3A8A] text-white" : "text-[#334155] hover:bg-white"}`}
            >
              All Care Seekers
            </button>
            {supervisorRoles.has(user.role) && (
              <button
                type="button"
                onClick={() => setAssignedFilter("unassigned")}
                data-testid="assigned-filter-unassigned"
                className={`px-3 py-1.5 rounded-full text-xs transition-colors ${assignedFilter === "unassigned" ? "bg-[#1E3A8A] text-white" : "text-[#334155] hover:bg-white"}`}
              >
                Unassigned
              </button>
            )}
          </div>
        </div>
      )}

      {/* Sort + stale toggles */}
      <div className="flex items-center gap-3 flex-wrap text-sm" data-testid="care-seekers-sort-row">
        <span className="text-xs text-[#1E3A8A] uppercase tracking-wider">Sort</span>
        <div className="inline-flex rounded-full bg-[#F9F8F6] border border-[#E5E5E0] p-0.5">
          <button
            onClick={() => setSortMode("recent")}
            data-testid="sort-recent"
            className={`px-3 py-1.5 rounded-full text-xs transition-colors ${sortMode === "recent" ? "bg-[#1E3A8A] text-white" : "text-[#334155] hover:bg-white"}`}
          >
            Most recent
          </button>
          <button
            onClick={() => setSortMode("oldest_contact")}
            data-testid="sort-oldest-contact"
            className={`px-3 py-1.5 rounded-full text-xs transition-colors ${sortMode === "oldest_contact" ? "bg-[#1E3A8A] text-white" : "text-[#334155] hover:bg-white"}`}
          >
            Oldest contact first
          </button>
        </div>
        <button
          onClick={() => setStaleOnly((s) => !s)}
          data-testid="stale-only-toggle"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            staleOnly
              ? "bg-[#FEF3C7] text-[#92400E] border border-[#FCD34D]"
              : "bg-white text-[#334155] border border-[#E5E5E0] hover:bg-[#F9F8F6]"
          }`}
        >
          <AlertTriangle className="w-3 h-3" strokeWidth={2} />
          {staleOnly ? "Showing stale only" : "Show stale follow-ups"}
        </button>
        <button
          onClick={() => setStaleReferralsOnly((s) => !s)}
          data-testid="stale-referrals-toggle"
          title="Care Seekers with referrals pending more than 14 days"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            staleReferralsOnly
              ? "bg-[#FEF3C7] text-[#92400E] border border-[#FCD34D]"
              : "bg-white text-[#334155] border border-[#E5E5E0] hover:bg-[#F9F8F6]"
          }`}
        >
          <AlertTriangle className="w-3 h-3" strokeWidth={2} />
          {staleReferralsOnly ? "Stale referrals only" : "Stale referrals"}
          {(() => {
            const n = items.reduce((acc, i) => acc + (i.stale_referrals_count || 0), 0);
            if (n === 0) return null;
            return (
              <span className={`ml-0.5 text-[10px] px-1.5 rounded-full ${
                staleReferralsOnly ? "bg-white text-[#92400E]" : "bg-[#FEF3C7] text-[#92400E]"
              }`}>
                {n}
              </span>
            );
          })()}
        </button>
        {staleOnly && (
          <span className="text-xs text-[#1E3A8A]">{visibleItems.length} {visibleItems.length === 1 ? "person" : "people"} need outreach</span>
        )}
        <div className="ml-auto inline-flex items-center gap-2">
          <button
            onClick={() => exportCareSeekersCsv(visibleItems)}
            disabled={visibleItems.length === 0}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#334155] text-xs font-medium hover:bg-[#F9F8F6] disabled:opacity-50 disabled:cursor-not-allowed"
            title={`Export ${visibleItems.length} Care Seekers as CSV`}
          >
            <Download className="w-3.5 h-3.5" strokeWidth={1.75} /> Export CSV
          </button>
          <div className="inline-flex rounded-full bg-[#F9F8F6] border border-[#E5E5E0] p-0.5" data-testid="cs-view-toggle">
            <button
              onClick={() => setViewMode("cards")}
              data-testid="view-cards"
              title="Card view"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                viewMode === "cards" ? "bg-[#1E3A8A] text-white" : "text-[#334155] hover:bg-white"
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" strokeWidth={1.75} /> Cards
            </button>
            <button
              onClick={() => setViewMode("list")}
              data-testid="view-list"
              title="List view"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                viewMode === "list" ? "bg-[#1E3A8A] text-white" : "text-[#334155] hover:bg-white"
              }`}
            >
              <LayoutList className="w-3.5 h-3.5" strokeWidth={1.75} /> List
            </button>
          </div>
        </div>
      </div>

      {channelFilter && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="active-channel-filter">
          <span className="text-xs text-[#1E3A8A]">Showing:</span>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium">
            <PhoneIncoming className="w-3 h-3" strokeWidth={2} />
            {channelFilter === "unspecified" ? "Unspecified channel" : CHANNEL_LABEL[channelFilter]}
            <button
              onClick={() => setChannelFilter("")}
              aria-label="Clear channel filter"
              data-testid="clear-channel-filter"
              className="ml-1 hover:bg-white/20 rounded-full"
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </span>
          <span className="text-xs text-[#1E3A8A]">{items.length} {items.length === 1 ? "match" : "matches"}</span>
          {(() => {
            const staleCount = items.filter((i) => {
              const isActive = i.status === "active" || i.status === "follow_up";
              if (!isActive) return false;
              if (!i.last_contact_at) return true;
              const d = daysSince(i.last_contact_at);
              return d != null && d > 7;
            }).length;
            if (staleCount === 0) return null;
            return (
              <span
                data-testid="stale-summary"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-xs font-medium"
              >
                <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                {staleCount} not contacted in &gt;7 days
              </span>
            );
          })()}
        </div>
      )}

      {/* Bulk-action bar (Phase 25) */}
      {selectedIds.size > 0 && can("care_seekers.edit") && (
        <BulkActionBar
          selectedCount={selectedIds.size}
          totalVisible={visibleItems.length}
          onSelectAll={() => selectAll(visibleItems.map((i) => i.id))}
          onClear={clearSelection}
          onAssign={() => setBulkAction("assign")}
          onFlag={() => setBulkAction("flag")}
          onExport={async () => {
            try {
              const ids = Array.from(selectedIds);
              const res = await api.post(
                "/care-seekers/bulk/export.csv",
                { ids },
                { responseType: "blob" }
              );
              const blob = new Blob([res.data], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = `care_seekers_selected_${ids.length}.csv`;
              document.body.appendChild(a); a.click();
              setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 800);
              toast.success(`Exported ${ids.length} Care Seekers`);
            } catch (e) { toast.error(formatApiError(e)); }
          }}
        />
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-sm text-[#1E3A8A]">Loading…</div>
      ) : visibleItems.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center">
          <Users className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-semibold text-[#0F172A] mb-1">No Care Seekers yet</h3>
          <p className="text-sm text-[#1E3A8A] mb-4">Add the first Care Seeker to begin coordinating support.</p>
          {canCreate && (
            <button
              onClick={() => setShowForm(true)}
              className="px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium"
              data-testid="empty-add-care-seeker"
            >
              Add Care Seeker
            </button>
          )}
        </div>
      ) : viewMode === "list" ? (
        <CareSeekersList
          items={visibleItems}
          canEdit={!!can("care_seekers.edit")}
          onEdit={setEditing}
          tableSort={tableSort}
          setTableSort={setTableSort}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelected}
          usersById={usersById}
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="care-seekers-list">
          {visibleItems.map((p) => (
            <Link
              key={p.id}
              to={`/care-seekers/${p.id}`}
              data-testid={`care-seeker-card-${p.id}`}
              className="bg-white border border-[#E5E5E0] rounded-2xl p-5 hw-card-hover block relative"
            >
              {can("care_seekers.edit") && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(p); }}
                  data-testid={`edit-careseeker-${p.id}`}
                  title="Edit Care Seeker"
                  className="absolute top-3 right-3 text-xs text-[#1E3A8A] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] hover:bg-white z-10"
                >
                  <Pencil className="w-3 h-3" /> Edit
                </button>
              )}
              <div className="flex items-start justify-between mb-3 pr-16">
                <div>
                  {p.display_id && (
                    <div className="font-mono text-[10px] tracking-wider text-[#1E3A8A] uppercase mb-1" data-testid={`csk-id-${p.id}`}>
                      {p.display_id}
                    </div>
                  )}
                  <h3 className="font-display text-lg font-semibold text-[#0F172A]">
                    {p.preferred_name || p.first_name} {p.last_name}
                  </h3>
                  {p.preferred_name && (
                    <div className="text-xs text-[#1E3A8A]">{p.first_name} {p.last_name}</div>
                  )}
                  {p.safety_flag?.active && (
                    <span
                      className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5] text-[10px] font-semibold uppercase tracking-wider"
                      data-testid={`safety-flag-pill-${p.id}`}
                    >
                      <AlertOctagon className="w-3 h-3" /> Safety
                    </span>
                  )}
                </div>
                <span className={`px-2 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider ${statusColor(p.status)}`}>
                  {p.status.replace(/_/g, " ")}
                </span>
              </div>
              <div className="space-y-1 text-sm text-[#334155]">
                {p.phone && <div>📞 {p.phone}</div>}
                {p.county && <div>📍 {p.county} County</div>}
                {p.dob && <div className="text-xs text-[#1E3A8A]">DOB: {formatDate(p.dob)}</div>}
              </div>
              {p.risk_flags && p.risk_flags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {p.risk_flags.slice(0, 3).map((flag) => (
                    <span key={flag} className="text-[10px] bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 rounded-full">
                      {flag}
                    </span>
                  ))}
                </div>
              )}
              {p.stale_referrals_count > 0 && (
                <div
                  data-testid={`stale-referrals-${p.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-[11px] bg-[#FEF3C7] text-[#92400E] px-2.5 py-1 rounded-full font-medium"
                  title="Pending referrals older than 14 days — needs follow-up"
                >
                  <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                  Needs follow-up · {p.stale_referrals_count} stale referral{p.stale_referrals_count > 1 ? "s" : ""}
                </div>
              )}
              {p.stale_goals_count > 0 && (
                <div
                  data-testid={`stale-goals-${p.id}`}
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] bg-[#DBEAFE] text-[#1E40AF] px-2.5 py-1 rounded-full font-medium"
                  title="Care plan goals in progress >30 days with no update — check in"
                >
                  <Target className="w-3 h-3" strokeWidth={2} />
                  Stale goal{p.stale_goals_count > 1 ? "s" : ""} · {p.stale_goals_count}
                </div>
              )}
              <LastContactRow entity={p} />
              <div className="mt-2 flex items-center gap-1.5 text-[11px]" data-testid={`cs-assigned-${p.id}`}>
                <UserCheck className={`w-3 h-3 ${p.assigned_to ? "text-[#1E3A8A]" : "text-[#92400E]"}`} strokeWidth={1.75} />
                <span className={p.assigned_to ? "text-[#1E3A8A]" : "text-[#92400E] font-medium"}>
                  {p.assigned_to ? `Assigned: ${usersById[p.assigned_to] || "—"}` : "Unassigned"}
                </span>
              </div>
              <MetadataFooter entity={p} className="mt-2 pt-2 border-t border-[#F0EFEB]" />
            </Link>
          ))}
        </div>
      )}

      {showForm && <CareSeekerForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      {editing && (
        <CareSeekerForm
          existing={editing}
          onClose={() => setEditing(null)}
          onCreated={() => { setEditing(null); load(); }}
        />
      )}

      {bulkAction === "assign" && (
        <BulkAssignDialog
          users={users}
          selectedCount={selectedIds.size}
          onClose={() => setBulkAction(null)}
          onConfirm={async (navigatorId) => {
            try {
              const ids = Array.from(selectedIds);
              const { data } = await api.post("/care-seekers/bulk/assign", { ids, assigned_to: navigatorId });
              toast.success(`Assigned ${data.updated} Care Seekers`);
              setBulkAction(null);
              clearSelection();
              load();
            } catch (e) { toast.error(formatApiError(e)); }
          }}
        />
      )}
      {bulkAction === "flag" && (
        <BulkFlagDialog
          selectedCount={selectedIds.size}
          onClose={() => setBulkAction(null)}
          onConfirm={async (flags, mode) => {
            try {
              const ids = Array.from(selectedIds);
              const { data } = await api.post("/care-seekers/bulk/flag", { ids, flags, mode });
              toast.success(`Flagged ${data.updated} Care Seekers`);
              setBulkAction(null);
              clearSelection();
              load();
            } catch (e) { toast.error(formatApiError(e)); }
          }}
        />
      )}
    </div>
  );
}

// ---------- Bulk action UI (Phase 25) ----------

function BulkActionBar({ selectedCount, totalVisible, onSelectAll, onClear, onAssign, onFlag, onExport }) {
  return (
    <div
      data-testid="bulk-action-bar"
      className="sticky top-2 z-20 bg-[#1E3A8A] text-white rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3 shadow-lg"
    >
      <div className="inline-flex items-center gap-2 text-sm font-medium">
        <CheckSquare className="w-4 h-4" />
        {selectedCount} selected
      </div>
      {selectedCount < totalVisible && (
        <button
          onClick={onSelectAll}
          data-testid="bulk-select-all"
          className="text-xs text-white/85 hover:text-white underline"
        >
          Select all {totalVisible} visible
        </button>
      )}
      <button
        onClick={onClear}
        data-testid="bulk-clear"
        className="text-xs text-white/85 hover:text-white underline"
      >
        Clear
      </button>
      <div className="ml-auto flex flex-wrap gap-2">
        <button
          onClick={onAssign}
          data-testid="bulk-assign-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#1E3A8A] text-xs font-medium hover:bg-[#F9F8F6]"
        >
          <UserCheck className="w-3.5 h-3.5" /> Assign navigator
        </button>
        <button
          onClick={onFlag}
          data-testid="bulk-flag-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#B45309] text-xs font-medium hover:bg-[#FEF3C7]"
        >
          <Flag className="w-3.5 h-3.5" /> Flag
        </button>
        <button
          onClick={onExport}
          data-testid="bulk-export-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[#334155] text-xs font-medium hover:bg-[#F9F8F6]"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
    </div>
  );
}

function BulkAssignDialog({ users, selectedCount, onClose, onConfirm }) {
  const [navigatorId, setNavigatorId] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="bulk-assign-dialog">
        <h2 className="font-display text-lg font-semibold text-[#0F172A] mb-1">Assign navigator</h2>
        <p className="text-sm text-[#334155] mb-4">Assign {selectedCount} selected Care Seekers to a navigator.</p>
        <select
          value={navigatorId}
          onChange={(e) => setNavigatorId(e.target.value)}
          data-testid="bulk-assign-select"
          className="w-full px-3 py-2 rounded-xl border border-[#E5E5E0] mb-4"
        >
          <option value="">— Unassign (clear navigator) —</option>
          {users.filter((u) => u.active !== false).map((u) => (
            <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
          ))}
        </select>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-[#E5E5E0] text-sm text-[#334155]">Cancel</button>
          <button
            onClick={() => onConfirm(navigatorId || null)}
            data-testid="bulk-assign-confirm"
            className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium"
          >
            {navigatorId ? "Assign" : "Unassign all"}
          </button>
        </div>
      </div>
    </div>
  );
}

const RISK_FLAG_OPTIONS = [
  { key: "high_risk", label: "High risk" },
  { key: "safety_concern", label: "Safety concern" },
  { key: "maternal_priority", label: "Maternal priority" },
  { key: "needs_follow_up", label: "Needs follow-up" },
  { key: "transportation_barrier", label: "Transportation barrier" },
];

function BulkFlagDialog({ selectedCount, onClose, onConfirm }) {
  const [picked, setPicked] = useState([]);
  const [mode, setMode] = useState("add");
  const togglePick = (k) => setPicked((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="bulk-flag-dialog">
        <h2 className="font-display text-lg font-semibold text-[#0F172A] mb-1">Flag {selectedCount} Care Seekers</h2>
        <p className="text-sm text-[#334155] mb-3">Pick the flags to apply, then choose how:</p>
        <div className="space-y-1.5 mb-4">
          {RISK_FLAG_OPTIONS.map((f) => (
            <label key={f.key} className="flex items-center gap-2 cursor-pointer text-sm text-[#334155]">
              <input
                type="checkbox"
                checked={picked.includes(f.key)}
                onChange={() => togglePick(f.key)}
                data-testid={`bulk-flag-opt-${f.key}`}
                className="w-4 h-4 rounded border-[#E5E5E0] text-[#1E3A8A] focus:ring-[#7E937D]"
              />
              {f.label}
            </label>
          ))}
        </div>
        <div className="flex items-center gap-2 mb-4 text-xs">
          <span className="text-[#1E3A8A] font-medium">Mode:</span>
          {[
            { v: "add", label: "Add (keep existing)" },
            { v: "set", label: "Replace existing" },
            { v: "remove", label: "Remove selected" },
          ].map((m) => (
            <button
              key={m.v}
              onClick={() => setMode(m.v)}
              data-testid={`bulk-flag-mode-${m.v}`}
              className={`px-3 py-1 rounded-full border ${
                mode === m.v ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "border-[#E5E5E0] text-[#334155]"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-[#E5E5E0] text-sm text-[#334155]">Cancel</button>
          <button
            disabled={picked.length === 0}
            onClick={() => onConfirm(picked, mode)}
            data-testid="bulk-flag-confirm"
            className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium disabled:opacity-50"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

function LastContactRow({ entity }) {
  const last = entity?.last_contact_at;
  const isActive = entity?.status === "active" || entity?.status === "follow_up";
  const days = daysSince(last);
  const stale = isActive && (last == null || (days != null && days > 7));
  const labelText = last ? `Last contact ${formatRelative(last)}` : "No contact logged yet";
  return (
    <div
      data-testid={`last-contact-${entity.id}`}
      className={`mt-3 pt-3 border-t border-[#F0EFEB] flex items-center gap-1.5 text-xs ${stale ? "text-[#92400E]" : "text-[#1E3A8A]"}`}
    >
      {stale ? (
        <AlertTriangle className="w-3 h-3 flex-shrink-0" strokeWidth={2} />
      ) : (
        <Clock className="w-3 h-3 flex-shrink-0" strokeWidth={1.75} />
      )}
      <span>{labelText}</span>
      {stale && last && <span className="text-[10px]">·</span>}
      {stale && last && <span className="text-[10px] uppercase tracking-wider font-medium">Stale</span>}
    </div>
  );
}

function SortHeader({ label, sortKey, tableSort, setTableSort, align = "left" }) {
  const active = tableSort?.key === sortKey;
  const dir = active ? tableSort.dir : null;
  const toggle = () => {
    if (!active) return setTableSort({ key: sortKey, dir: "asc" });
    if (dir === "asc") return setTableSort({ key: sortKey, dir: "desc" });
    setTableSort(null); // third click clears -> falls back to chip sort
  };
  return (
    <th className={`px-4 py-3 font-medium text-${align}`}>
      <button
        onClick={toggle}
        data-testid={`sort-col-${sortKey}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wider text-[10px] transition-colors ${
          active ? "text-[#1E3A8A]" : "text-[#1E3A8A] hover:text-[#334155]"
        }`}
      >
        {label}
        {active && (dir === "asc" ? <ArrowUp className="w-3 h-3" strokeWidth={2} /> : <ArrowDown className="w-3 h-3" strokeWidth={2} />)}
      </button>
    </th>
  );
}

function CareSeekersList({ items, canEdit, onEdit, tableSort, setTableSort, selectedIds, onToggleSelect, usersById = {} }) {
  const allSelected = items.length > 0 && items.every((i) => selectedIds?.has(i.id));
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden" data-testid="care-seekers-list-view">
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F9F8F6]">
              {canEdit && (
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => {
                      if (allSelected) items.forEach((i) => selectedIds?.has(i.id) && onToggleSelect(i.id));
                      else items.forEach((i) => !selectedIds?.has(i.id) && onToggleSelect(i.id));
                    }}
                    data-testid="cs-select-all"
                    aria-label="Select all visible"
                    className="w-4 h-4 rounded border-[#E5E5E0] text-[#1E3A8A]"
                  />
                </th>
              )}
              <SortHeader label="ID" sortKey="display_id" tableSort={tableSort} setTableSort={setTableSort} />
              <SortHeader label="Name" sortKey="name" tableSort={tableSort} setTableSort={setTableSort} />
              <SortHeader label="Status" sortKey="status" tableSort={tableSort} setTableSort={setTableSort} />
              <SortHeader label="Phone" sortKey="phone" tableSort={tableSort} setTableSort={setTableSort} />
              <SortHeader label="County" sortKey="county" tableSort={tableSort} setTableSort={setTableSort} />
              <SortHeader label="Channel" sortKey="channel" tableSort={tableSort} setTableSort={setTableSort} />
              <SortHeader label="Last contact" sortKey="last_contact" tableSort={tableSort} setTableSort={setTableSort} />
              <th className="px-4 py-3 text-left">
                <span className="inline-flex items-center uppercase tracking-wider text-[10px] text-[#1E3A8A]">Assigned</span>
              </th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const isActive = p.status === "active" || p.status === "follow_up";
              const days = daysSince(p.last_contact_at);
              const stale = isActive && (p.last_contact_at == null || (days != null && days > 7));
              const channel = p.intake_channel ? CHANNEL_LABEL[p.intake_channel] || p.intake_channel : "—";
              return (
                <tr
                  key={p.id}
                  data-testid={`cs-row-${p.id}`}
                  className="border-t border-[#F0EFEB] hover:bg-[#F9F8F6] transition-colors group"
                >
                  {canEdit && (
                    <td className="px-3 py-3 align-top">
                      <input
                        type="checkbox"
                        checked={selectedIds?.has(p.id) || false}
                        onChange={() => onToggleSelect(p.id)}
                        data-testid={`cs-select-${p.id}`}
                        aria-label={`Select ${p.first_name} ${p.last_name}`}
                        className="w-4 h-4 rounded border-[#E5E5E0] text-[#1E3A8A]"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 align-top">
                    {p.display_id ? (
                      <Link
                        to={`/care-seekers/${p.id}`}
                        className="font-mono text-[11px] tracking-wider text-[#1E3A8A] bg-[#E0E7FF] px-2 py-0.5 rounded-full inline-block hover:bg-[#C7D2FE]"
                      >
                        {p.display_id}
                      </Link>
                    ) : <span className="text-[#1E3A8A]">—</span>}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <Link to={`/care-seekers/${p.id}`} className="block group-hover:text-[#1E3A8A]">
                      <div className="font-medium text-[#0F172A]">{p.preferred_name || p.first_name} {p.last_name}</div>
                      {p.preferred_name && (
                        <div className="text-[11px] text-[#1E3A8A]">{p.first_name} {p.last_name}</div>
                      )}
                      {p.safety_flag?.active && (
                        <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5] text-[9px] font-semibold uppercase tracking-wider" data-testid={`safety-flag-table-${p.id}`}>
                          <AlertOctagon className="w-2.5 h-2.5" /> Safety
                        </div>
                      )}
                      {p.risk_flags && p.risk_flags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {p.risk_flags.slice(0, 2).map((flag) => (
                            <span key={flag} className="text-[9px] bg-[#FEF3C7] text-[#92400E] px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                              {flag}
                            </span>
                          ))}
                          {p.risk_flags.length > 2 && (
                            <span className="text-[9px] text-[#1E3A8A]">+{p.risk_flags.length - 2}</span>
                          )}
                        </div>
                      )}
                      {p.stale_referrals_count > 0 && (
                        <div
                          data-testid={`stale-referrals-row-${p.id}`}
                          className="mt-1 inline-flex items-center gap-1 text-[10px] bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 rounded-full font-medium"
                          title="Pending referrals older than 14 days — needs follow-up"
                        >
                          <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                          Needs follow-up · {p.stale_referrals_count}
                        </div>
                      )}
                      {p.stale_goals_count > 0 && (
                        <div
                          data-testid={`stale-goals-row-${p.id}`}
                          className="mt-1 ml-1 inline-flex items-center gap-1 text-[10px] bg-[#DBEAFE] text-[#1E40AF] px-2 py-0.5 rounded-full font-medium"
                        >
                          <Target className="w-3 h-3" strokeWidth={2} />
                          Stale goal · {p.stale_goals_count}
                        </div>
                      )}
                    </Link>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${statusColor(p.status)}`}>
                      {p.status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top text-[#334155] whitespace-nowrap">{p.phone || <span className="text-[#1E3A8A]">—</span>}</td>
                  <td className="px-4 py-3 align-top text-[#334155]">{p.county ? `${p.county} County` : <span className="text-[#1E3A8A]">—</span>}</td>
                  <td className="px-4 py-3 align-top text-[#334155]">
                    {p.intake_channel ? (
                      <span className="inline-flex items-center gap-1 text-[11px]">
                        <PhoneIncoming className="w-3 h-3 text-[#1E3A8A]" strokeWidth={1.75} />
                        {channel}
                      </span>
                    ) : <span className="text-[#1E3A8A]">—</span>}
                  </td>
                  <td className={`px-4 py-3 align-top text-[11px] whitespace-nowrap ${stale ? "text-[#92400E]" : "text-[#1E3A8A]"}`}>
                    {stale && <AlertTriangle className="w-3 h-3 inline mr-1" strokeWidth={2} />}
                    {p.last_contact_at ? formatRelative(p.last_contact_at) : "No contact yet"}
                  </td>
                  <td className="px-4 py-3 align-top text-[11px] whitespace-nowrap" data-testid={`cs-row-assigned-${p.id}`}>
                    {p.assigned_to ? (
                      <span className="text-[#1E3A8A]">{usersById[p.assigned_to] || "—"}</span>
                    ) : (
                      <span className="text-[#92400E] font-medium">Unassigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => onEdit(p)}
                        data-testid={`row-edit-${p.id}`}
                        title="Edit Care Seeker"
                        className="text-xs text-[#1E3A8A] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] hover:bg-white"
                      >
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul className="md:hidden divide-y divide-[#F0EFEB]">
        {items.map((p) => {
          const isActive = p.status === "active" || p.status === "follow_up";
          const days = daysSince(p.last_contact_at);
          const stale = isActive && (p.last_contact_at == null || (days != null && days > 7));
          return (
            <li key={p.id} className="p-3 flex items-start justify-between gap-3" data-testid={`cs-row-mobile-${p.id}`}>
              <Link to={`/care-seekers/${p.id}`} className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {p.display_id && <span className="font-mono text-[10px] tracking-wider text-[#1E3A8A] bg-[#E0E7FF] px-1.5 py-0.5 rounded-full">{p.display_id}</span>}
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-medium uppercase tracking-wider ${statusColor(p.status)}`}>
                    {p.status.replace(/_/g, " ")}
                  </span>
                </div>
                <div className="font-medium text-[#0F172A] mt-1">{p.preferred_name || p.first_name} {p.last_name}</div>
                <div className="text-xs text-[#1E3A8A] mt-0.5">
                  {p.phone || "—"}{p.county ? ` · ${p.county} County` : ""}
                </div>
                <div className={`text-[11px] mt-1 ${stale ? "text-[#92400E]" : "text-[#1E3A8A]"}`}>
                  {stale && <AlertTriangle className="w-3 h-3 inline mr-1" strokeWidth={2} />}
                  {p.last_contact_at ? `Last: ${formatRelative(p.last_contact_at)}` : "No contact yet"}
                </div>
                <div className={`text-[11px] mt-0.5 ${p.assigned_to ? "text-[#1E3A8A]" : "text-[#92400E] font-medium"}`} data-testid={`cs-row-mobile-assigned-${p.id}`}>
                  {p.assigned_to ? `Assigned: ${usersById[p.assigned_to] || "—"}` : "Unassigned"}
                </div>
                {p.stale_referrals_count > 0 && (
                  <div
                    data-testid={`stale-referrals-mobile-${p.id}`}
                    className="mt-1 inline-flex items-center gap-1 text-[10px] bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 rounded-full font-medium"
                  >
                    <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                    Needs follow-up · {p.stale_referrals_count}
                  </div>
                )}
                {p.stale_goals_count > 0 && (
                  <div
                    data-testid={`stale-goals-mobile-${p.id}`}
                    className="mt-1 ml-1 inline-flex items-center gap-1 text-[10px] bg-[#DBEAFE] text-[#1E40AF] px-2 py-0.5 rounded-full font-medium"
                  >
                    <Target className="w-3 h-3" strokeWidth={2} />
                    Stale goal · {p.stale_goals_count}
                  </div>
                )}
              </Link>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(p)}
                  data-testid={`row-edit-mobile-${p.id}`}
                  className="p-2 rounded-full text-[#1E3A8A] hover:bg-[#F9F8F6]"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


export function CareSeekerForm({ onClose, onCreated, existing }) {
  const [form, setForm] = useState({
    first_name: existing?.first_name || "",
    last_name: existing?.last_name || "",
    preferred_name: existing?.preferred_name || "",
    dob: existing?.dob || "",
    phone: existing?.phone || "",
    email: existing?.email || "",
    address: existing?.address || "",
    city: existing?.city || "",
    county: existing?.county || "",
    state: existing?.state || "NC",
    zip_code: existing?.zip_code || "",
    preferred_contact: existing?.preferred_contact || "phone",
    household_size: existing?.household_size ?? "",
    referral_source: existing?.referral_source || "",
    intake_channel: existing?.intake_channel || "",
    status: existing?.status || "active",
    notes: existing?.notes || "",
    consent_acknowledged: existing?.consent_acknowledged || false,
    reminder_date: existing?.reminder_date || "",
    reminder_note: existing?.reminder_note || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!existing;

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.household_size === "") delete payload.household_size;
      else payload.household_size = Number(payload.household_size);
      if (isEdit) {
        await api.patch(`/care-seekers/${existing.id}`, payload);
        toast.success("Care Seeker updated");
      } else {
        await api.post("/care-seekers", payload);
        toast.success("Care Seeker added");
      }
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-6 py-4 flex justify-between items-center">
          <h2 className="font-display text-xl font-semibold text-[#0F172A]">
            {isEdit ? "Edit Care Seeker" : "Add Care Seeker"}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F9F8F6] rounded-full" data-testid="close-form-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="care-seeker-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="First name" required value={form.first_name} onChange={(v) => update("first_name", v)} testId="cs-first-name" />
            <Field label="Last name" required value={form.last_name} onChange={(v) => update("last_name", v)} testId="cs-last-name" />
            <Field label="Preferred name" value={form.preferred_name} onChange={(v) => update("preferred_name", v)} testId="cs-pref-name" />
            <Field label="Date of birth" type="date" value={form.dob} onChange={(v) => update("dob", v)} testId="cs-dob" />
            <Field label="Phone" value={form.phone} onChange={(v) => update("phone", v)} testId="cs-phone" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => update("email", v)} testId="cs-email" />
            <Field label="Address" value={form.address} onChange={(v) => update("address", v)} testId="cs-address" />
            <Field label="City" value={form.city} onChange={(v) => update("city", v)} testId="cs-city" />
            <Field label="County" value={form.county} onChange={(v) => update("county", v)} testId="cs-county" />
            <Field label="ZIP" value={form.zip_code} onChange={(v) => update("zip_code", v)} testId="cs-zip" />
            <SelectField label="Preferred contact" value={form.preferred_contact} onChange={(v) => update("preferred_contact", v)} testId="cs-contact">
              <option value="phone">Phone</option>
              <option value="email">Email</option>
              <option value="text">Text</option>
            </SelectField>
            <Field label="Household size" type="number" value={form.household_size} onChange={(v) => update("household_size", v)} testId="cs-household" />
          </div>
          <Field label="Referral source" value={form.referral_source} onChange={(v) => update("referral_source", v)} testId="cs-ref-source" />
          <SelectField label="How did they reach us?" value={form.intake_channel} onChange={(v) => update("intake_channel", v)} testId="cs-intake-channel">
            <option value="">— Select channel —</option>
            {INTAKE_CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </SelectField>
          <SelectField label="Status" value={form.status} onChange={(v) => update("status", v)} testId="cs-status">
            {CARE_SEEKER_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </SelectField>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Initial notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              rows={3}
              data-testid="cs-notes"
              className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D]"
            />
          </div>
          <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-xl p-4 space-y-3" data-testid="cs-reminder-section">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded-lg bg-white text-[#1E3A8A] flex items-center justify-center flex-shrink-0">
                <Bell className="w-4 h-4" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-sm font-medium text-[#0F172A]">Reminder</div>
                <div className="text-xs text-[#1E3A8A]">Tickle me on this date — appears on the dashboard for everyone.</div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field
                label="Reminder date"
                type="date"
                value={form.reminder_date}
                onChange={(v) => update("reminder_date", v)}
                testId="cs-reminder-date"
              />
              <Field
                label="Reminder note"
                value={form.reminder_note}
                onChange={(v) => update("reminder_note", v)}
                testId="cs-reminder-note"
              />
            </div>
          </div>
          <label className="flex items-start gap-3 p-3 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0]">
            <input
              type="checkbox"
              checked={form.consent_acknowledged}
              onChange={(e) => update("consent_acknowledged", e.target.checked)}
              data-testid="cs-consent"
              className="mt-1"
            />
            <span className="text-sm text-[#334155]">
              The Care Seeker has been informed of, and verbally consented to, HarborWell's
              support and privacy practices.
            </span>
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-[#E5E5E0] text-[#334155] font-medium">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="cs-submit-btn"
              className="flex-1 py-3 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554] disabled:opacity-60"
            >
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Save Care Seeker"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export const Field = ({ label, type = "text", value, onChange, required, testId }) => (
  <div>
    <label className="block text-sm font-medium text-[#334155] mb-2">
      {label}{required && <span className="text-red-500">*</span>}
    </label>
    <input
      data-testid={testId}
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D]"
    />
  </div>
);

export const SelectField = ({ label, value, onChange, children, testId }) => (
  <div>
    <label className="block text-sm font-medium text-[#334155] mb-2">{label}</label>
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D]"
    >
      {children}
    </select>
  </div>
);
